import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { QueryState } from '@client/components/_ui/feedback/QueryState';
import {
    CompletionByHandlingPartyCard,
    LongestOverdueCard,
    OutstandingStrip,
    TrendTiles,
} from '@client/components/attestations/reporting/OverviewSections';
import { RoundsCard } from '@client/components/attestations/reporting/RoundsCard';
import { usePermissions } from '@client/hooks/auth/usePermissions';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpc } from '@client/trpc/client';
import { ATTESTATION_REPORTING_PERIODS } from '@shared/constants/business/user-attestation';
import { hasPermission } from '@shared/permissions/roles';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Select } from 'antd';
import { ChartColumn, Send } from 'lucide-react';
import { z } from 'zod';

const OverviewSearchSchema = z.object({
    /** A form family (`schemaKey`); absent = every form. */
    form: z.string().max(200).optional().catch(undefined),
    /** A handling party id; absent = every recipient. */
    handlingParty: z.string().regex(/^\d+$/).optional().catch(undefined),
    period: z
        .number()
        .int()
        .refine((value) => (ATTESTATION_REPORTING_PERIODS as readonly number[]).includes(value))
        .optional()
        .catch(undefined),
});

export const Route = createFileRoute('/console/$orgId/attestations/overview')({
    staticData: {
        metadata: {
            title: 'Attestations overview',
        },
        modules: ['attestations'],
        roles: ['org_admin', 'attestations_user'],
        featureFlag: 'enable-attestation-reporting',
        ui: {
            title: 'Attestations overview',
            subtitle: "Who has attested, who hasn't and what's left",
            icon: ChartColumn,
        },
    },
    validateSearch: zodValidator(OverviewSearchSchema),
    component: AttestationsOverview,
});

const DEFAULT_PERIOD = 90;

function AttestationsOverview() {
    const orgId = useOrgId();
    const navigate = useNavigate();
    const user = useUser();
    const { can } = usePermissions();
    const search = Route.useSearch();
    const periodDays = search.period ?? DEFAULT_PERIOD;
    const scope = {
        formSchemaKeys: search.form ? [search.form] : undefined,
        handlingPartyIds: search.handlingParty ? [BigInt(search.handlingParty)] : undefined,
        periodDays,
    };
    const registerScope = { formSchemaKey: search.form, handlingPartyId: search.handlingParty };

    const overviewQuery = trpc.userAttestation.getAttestationOverview.useQuery(scope, {
        meta: { errorSurface: 'inline' },
    });
    const handlingPartiesQuery = trpc.handlingParty.listHandlingParties.useQuery({
        where: hasPermission(user.roles, 'org:admin') ? {} : { UsersInHandlingParty: { some: { userId: user.id } } },
    });

    const setSearch = (next: Partial<z.infer<typeof OverviewSearchSchema>>) =>
        navigate({
            to: '/console/$orgId/attestations/overview',
            params: { orgId },
            search: (previous) => ({ ...previous, ...next }),
            replace: true,
        });

    return (
        <PageController route={Route}>
            <div className="flex flex-col gap-5">
                <div className="flex flex-wrap items-center gap-2">
                    <Select
                        aria-label="Form"
                        className="w-64"
                        value={search.form ?? ''}
                        onChange={(value) => setSearch({ form: value || undefined })}
                        options={[
                            { value: '', label: 'All forms' },
                            ...(overviewQuery.data?.forms ?? []).map((form) => ({
                                value: form.schemaKey,
                                label: form.formTitle,
                            })),
                        ]}
                    />
                    <Select
                        aria-label="Handling party"
                        className="w-56"
                        value={search.handlingParty ?? ''}
                        onChange={(value) => setSearch({ handlingParty: value || undefined })}
                        showSearch
                        optionFilterProp="label"
                        options={[
                            { value: '', label: 'All handling parties' },
                            ...(handlingPartiesQuery.data?.rows ?? []).map((party) => ({
                                value: String(party.id),
                                label: party.name,
                            })),
                        ]}
                    />
                    <Select
                        aria-label="Period"
                        className="w-40"
                        value={periodDays}
                        onChange={(value) => setSearch({ period: value === DEFAULT_PERIOD ? undefined : value })}
                        options={ATTESTATION_REPORTING_PERIODS.map((days) => ({
                            value: days,
                            label: days === 365 ? 'Last 12 months' : `Last ${days} days`,
                        }))}
                    />
                    <span className="text-subtle-foreground text-xs">
                        The period applies to trends and finished rounds. Outstanding counts are always live.
                    </span>
                    {can('user_attestation:create') && (
                        <Button.Link
                            to="/console/$orgId/attestations/user-attestations/send"
                            params={{ orgId }}
                            variant="primary"
                            icon={<Send />}
                            className="ml-auto"
                        >
                            Send user attestation
                        </Button.Link>
                    )}
                </div>

                <QueryState query={overviewQuery} loading={<Loading />} errorTitle="The overview could not be loaded">
                    {(overview) => (
                        <>
                            <OutstandingStrip
                                outstanding={overview.outstanding}
                                timezone={overview.timezone}
                                scope={registerScope}
                            />
                            <TrendTiles overview={overview} scope={registerScope} />
                        </>
                    )}
                </QueryState>

                <RoundsCard scope={scope} />

                {overviewQuery.data && (
                    <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
                        <LongestOverdueCard
                            rows={overviewQuery.data.longestOverdue}
                            canUpdate={can('user_attestation:update')}
                            scope={registerScope}
                        />
                        <CompletionByHandlingPartyCard overview={overviewQuery.data} />
                    </div>
                )}
            </div>
            <Outlet />
        </PageController>
    );
}
